import {STATUS} from '@jobseeker/common-core/constants';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';
import deduplicateRequest from '@jobseeker/common-core/helpers/deduplicateRequest';

const axios = axiosInterceptorInstance(
  process.env.NEXT_PUBLIC_COURSES_API_BASE_URL
);

// TODO: Combine the functions into one, as the code is getting repeated
const getCourses = async ({
  userId,
  token = '',
  headers = {},
  params = {},
}: {
  userId: number;
  token?: string;
  headers?: object;
  params?: object;
}): Promise<any> => {
  try {
    const requestHeaders = Object.assign(
      token?.length > 0 ? {Authorization: `Bearer ${token}`} : {},
      headers
    );
    const response: any = await axios.get(`/v2/course/${userId}/search`, {
      headers: requestHeaders,
      params,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getUnpublishedCourses = async ({
  params = {},
}: {
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/course/unpublish', {params});
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getCourseDetails = async ({
  coursePublishedId,
  userId,
  headers,
  refinv,
  reforg,
  forUnpublishedCourse = false,
  token = '',
}: {
  coursePublishedId: number;
  userId: number;
  headers?: object;
  refinv?: string;
  reforg?: string;
  forUnpublishedCourse?: boolean;
  token?: string;
}): Promise<any> => {
  try {
    const apiPath = forUnpublishedCourse
      ? `/v2/course/unpublish/${coursePublishedId}/detail`
      : `/v2/course/${userId}/user/${coursePublishedId}/detail`;

    const requestHeaders = Object.assign(
      token?.length > 0 ? {Authorization: `Bearer ${token}`} : {},
      headers
    );
    const response: any = await axios.get(apiPath, {
      headers: requestHeaders,
      params: {refinv, reforg},
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const downloadBrochure = async ({
  coursePublishedId,
  refinv = 'NA',
  reforg = 'NA',
}: {
  coursePublishedId: number;
  refinv?: string;
  reforg?: string;
}): Promise<any> => {
  try {
    const response = await axios.get(
      `/v2/course/${coursePublishedId}/brochure`,
      {
        params: {
          refinv,
          reforg,
        },
      }
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const enquireCourse = async ({
  coursePublishedId,
  refinv = 'NA',
  reforg = 'NA',
}: {
  coursePublishedId: number;
  refinv?: string;
  reforg?: string;
}): Promise<any> => {
  try {
    const response = await axios.get(
      `/v2/course/${coursePublishedId}/enquiry`,
      {
        params: {
          refinv,
          reforg,
        },
      }
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getAllCategoriesCourses = async () => {
  try {
    const response: any = await axios.get('/v2/homePage/allcat');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getCategoryCourses = async ({
  categoryId,
  blackListedCourse,
}: {
  categoryId: number;
  blackListedCourse?: string;
}) => {
  try {
    const response: any = await axios.get(`/v2/courses/${categoryId}`, {
      params: {...(blackListedCourse && {blackListedCourse: 1})},
    });

    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const enquireMultipleCourses = async ({
  coursePublishedIds,
  toSkipIds,
  refinv = 'NA',
  reforg = 'NA',
  device = 'Web',
}: {
  coursePublishedIds: number[];
  toSkipIds: number[];
  refinv?: string;
  reforg?: string;
  device?: string;
}) => {
  try {
    if (coursePublishedIds.length === 0) {
      throw {
        response: {
          data: {
            error_msg: 'Please select a course',
            status: 400,
          },
        },
      };
    }

    const response = await axios.post('/v2/courseenquiry', {
      courseIds: JSON.stringify(coursePublishedIds),
      toSkipIds: JSON.stringify(toSkipIds),
      refinv,
      reforg,
      device,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error_msg,
        code: error.response?.data.status,
      },
    };
  }
};

const getTopInstitutes = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/topinstitute');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getTrendingCourses = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/trending');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getBanner = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/banner');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getFeaturedCourses = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/featured');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getJobReadyCourses = async ({
  userId,
  jobId,
  headers = {},
  params = {},
}: {
  userId: number;
  jobId: number;
  headers?: object;
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get(
      `/v2/course/${userId}/jobready/${jobId}`,
      {headers, params}
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getCoursesAfterJobApply = async ({
  headers = {},
  params = {},
}: {
  headers?: object;
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/course/jobready', {
      headers,
      params,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getInFeedCourses = deduplicateRequest(
  'in-feed-courses',
  async ({
    headers = {},
    params = {},
  }: {
    headers?: object;
    params?: object;
  }): Promise<any> => {
    try {
      const response: any = await axios.get('/v2/billboard/in-feed-courses', {
        headers,
        params,
      });
      if (response[STATUS] === 200) {
        return response.data;
      }
      return {data: {}};
    } catch (error: any) {
      return {
        error: {
          message: error.response?.data.error.message,
          code: error.response?.data.error.statusCode,
        },
      };
    }
  }
);

const getCoursesList = deduplicateRequest(
  'courses-list',
  async ({
    params = {},
  }: {
    params?: object;
  }): Promise<any> => {
    try {
      const response: any = await axios.get('/v2/courses-list', {params});
      if (response[STATUS] === 200) {
        return response.data;
      }
      return {
        status: {
          code: '10200',
          message: 'Operation performed successfully',
        },
        data: {
          adList: [],
          slotTitle: 'Learn & Grow',
        },
      };
    } catch (error: any) {
      return {
        error: {
          message: error.response?.data.error.message,
          code: error.response?.data.error.statusCode,
        },
      };
    }
  }
);

const getFeaturedInstitutes = async ({
  params = {},
}: {
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/billboard/featured-institutes', {
      params,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {
      status: {
        code: '10200',
        message: 'Operation performed successfully',
      },
      data: {
        adList: [],
        slotTitle: 'Featured Institute',
      },
    };
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getRecommendedAndTrendingCourses = async ({
  courseId,
  headers = {},
  params = {},
}: {
  courseId: number;
  headers?: object;
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get(`/v2/course/${courseId}/similar`, {
      headers,
      params,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {
      status: {
        code: '10200',
        message: 'Operation performed successfully',
      },
      data: {
        adList: {
          recommended: [],
          trending: [],
        },
        slotTitle: 'Trending Courses',
      },
    };
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getCoursesByTags = async ({
  headers = {},
  params = {},
}: {
  headers?: object;
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/course/searchByTags', {
      headers,
      params,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {
      status: {
        code: '10200',
        message: 'Operation performed successfully',
      },
      data: {
        adList: [],
        slotTitle: 'Trending Courses in Search Popup',
      },
    };
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const courseService = {
  getCourses,
  enquireCourse,
  downloadBrochure,
  getCourseDetails,
  enquireMultipleCourses,
  getAllCategoriesCourses,
  getCategoryCourses,
  getTopInstitutes,
  getTrendingCourses,
  getBanner,
  getFeaturedCourses,
  getUnpublishedCourses,
  getJobReadyCourses,
  getRecommendedAndTrendingCourses,
  getCoursesList,
  getFeaturedInstitutes,
  getCoursesAfterJobApply,
  getInFeedCourses,
  getCoursesByTags,
};

export default courseService;
